import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import EditOutlined from '@ant-design/icons/EditOutlined';
import LeftOutlined from '@ant-design/icons/LeftOutlined';

import IEP from 'pages/inclusionhead/iep';
import { StudentRegistrationProvider, useStudentRegistration } from 'contexts/StudentRegistrationContext';
import { getIepVersion } from 'services/api/iep';
import { getClient, getClientRegistration } from 'services/api/clients';
import { serviceLabel } from 'utils/goalServices';

// Build a sharedData patch from the client's saved enquiry/intake so the
// IEP form's Report Identification block can pre-fill Child Name, Child ID,
// DOB, Parent(s) Name, Address, Services from prior onboarding. Mirrors
// HydratedReadOnlyForm's logic but also composes parentNames + address into
// the single-line shapes the IEP form expects.
function buildIepPrefillFromRegistration(registration, client) {
  const updates = {};
  const inq = registration?.inquiry_data || null;
  const intakeCd = registration?.intake_data?.child_demographics || {};

  // Child Name
  const childNameFromInq = inq ? `${inq.child_first_name || ''} ${inq.child_last_name || ''}`.trim() : '';
  const childNameFromClient = client ? `${client.first_name || ''} ${client.last_name || ''}`.trim() : '';
  const childName = childNameFromInq || childNameFromClient;
  if (childName) updates.childName = childName;

  // Child ID — prefer the externally-entered code on intake, then iep_data,
  // then the client's `client_code`.
  const externalChildId = intakeCd.child_id || registration?.iep_data?.childId || client?.client_code;
  if (externalChildId) updates.childId = externalChildId;

  // Date of Birth
  const dob = inq?.child_date_of_birth || client?.date_of_birth;
  if (dob) updates.dateOfBirth = dob;

  // Gender (used by other forms but harmless here)
  if (inq?.child_gender) updates.gender = inq.child_gender;
  else if (client?.gender) updates.gender = client.gender;

  // Parents — compose father/mother into a single line for the IEP form.
  const fatherFromInq = inq ? `${inq.parent_first_name || ''} ${inq.parent_last_name || ''}`.trim() : '';
  if (fatherFromInq) updates.fatherName = fatherFromInq;
  if (inq?.parent_phone) updates.fatherMobile = inq.parent_phone;
  if (inq?.parent_email) updates.fatherEmail = inq.parent_email;
  if (inq?.parent_alternate_phone) updates.motherMobile = inq.parent_alternate_phone;
  const motherName = intakeCd.mother_name || registration?.intake_data?.family_information?.mother_name || '';
  if (motherName) updates.motherName = motherName;
  const parentNames = [fatherFromInq, motherName].filter(Boolean).join(' / ');
  if (parentNames) updates.parentNames = parentNames;

  // Address — compose multi-line address into a single string for the
  // IEP form's "Address" textarea.
  const street = inq?.address_line1 || '';
  const city = inq?.city || '';
  const state = inq?.state || '';
  const zip = inq?.postal_code || '';
  if (street) updates.streetAddress = street;
  if (city) updates.city = city;
  if (state) updates.state = state;
  if (zip) updates.zipCode = zip;
  const addressParts = [street, city, state, zip].filter(Boolean);
  if (addressParts.length > 0) updates.address = addressParts.join(', ');

  // Services — what the parent actually asked for at enquiry.
  //
  // `type` is the broad bucket ("therapy"); `sub` is the SERVICE under it
  // ("speech_therapy,occupational_therapy"), which is the question an IEP is
  // answering. Prefilling the bucket put the word "therapy" on a speech IEP's
  // Services line and told the clinician nothing; the service names say which
  // programme this document is for, and they are the same codes a goal stores in
  // service_code, so what the enquiry recorded and what the goals are written
  // under can be read side by side.
  //
  // Names rather than codes because iep.services is a free-text line a clinician
  // finishes by hand — "Speech Therapy 2 hours per day, 5 days a week".
  const serviceCodes = String(inq?.preferred_services?.sub || '')
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
  const serviceNames = serviceCodes
    .map((code) => {
      const name = serviceLabel(code);
      // serviceLabel echoes back anything outside GOAL_SERVICES (shadow_teacher,
      // developmental_assessment — real enquiry answers with no service row).
      return name === code ? code.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()) : name;
    })
    .join(', ');

  const lookingFor = inq?.preferred_services?.type || '';
  // Falls back to the bucket for enquiries saved before Service was mandatory.
  const services = serviceNames || lookingFor;
  if (services) updates.services = services;
  // Mirror into `lookingFor` so the enquiry form's own field also has it. This one
  // stays the TYPE — it is that dropdown's own value, not a service name.
  if (lookingFor) updates.lookingFor = lookingFor;

  // Supervisor — not collected during enquiry/intake; leave blank for the
  // clinician to fill in. (When the backend assignment data is available we
  // can wire `client.supervising_bcba_id` to a friendly name here.)

  // Primary concern flow-through (handy for other forms).
  if (inq?.primary_diagnosis) updates.primaryConcern = inq.primary_diagnosis;

  return updates;
}

/**
 * Page that wires the existing IEP form into the new clinical.iep_version
 * workflow. Three modes selected by URL:
 *   - new:   /supervisor/child/:childId/iep/new
 *   - view:  /supervisor/child/:childId/iep/version/:versionId
 *   - edit:  /supervisor/child/:childId/iep/version/:versionId/edit
 *
 * The persona base path is provided by the route so navigation/cancel
 * returns to the right child-detail page (supervisor vs. therapist).
 *
 * `view` renders the IEP form read-only with prefilled data and exposes
 * an "Edit" CTA that flips into the edit URL. Save behavior:
 *   - `new`  → createClientIepVersion()  (adds a new version row)
 *   - `edit` → updateIepVersion()        (modifies the current row in place)
 */
function IepVersionEditorInner({ personaBasePath, mode }) {
  const { childId, versionId } = useParams();
  const navigate = useNavigate();
  const { saveFormData, updateSharedData } = useStudentRegistration();

  // Hydrate on initial load whether we're loading a saved version OR a fresh
  // new IEP — for "new" we still need to pull enquiry/intake into shared data.
  const [hydrating, setHydrating] = useState(true);
  const [error, setError] = useState(null);

  const intl = useIntl();
  const fmt = (id) => intl.formatMessage({ id });

  const isView = mode === 'view';
  const returnPath = `${personaBasePath}/child/${childId}/iep-documents`;

  useEffect(() => {
    // Always make sure the StudentRegistration context knows which client
    // we're operating on — handleSave uses sharedData.clientId.
    updateSharedData({ clientId: childId });
  }, [childId, updateSharedData]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setHydrating(true);

        // Pre-fill shared data from the client's enquiry/intake so the IEP
        // form's Report Identification section auto-populates Child Name /
        // Child ID / DOB / Parent(s) Name / Address / Services. This runs
        // in all modes (new, view, edit) so the IEP form picks up the
        // freshest enquiry/intake values via getSharedFieldsForForm('iep').
        try {
          const [client, registration] = await Promise.all([
            getClient(childId).catch(() => null),
            getClientRegistration(childId).catch(() => null)
          ]);
          if (cancelled) return;
          const updates = buildIepPrefillFromRegistration(registration, client);
          if (Object.keys(updates).length > 0) updateSharedData(updates);
        } catch (prefillErr) {
          // Pre-fill is best-effort — don't block the editor on it.
          console.warn('[IepVersionEditor] prefill skipped:', prefillErr?.message);
        }

        // When opening an existing version, also seed the verbatim form blob
        // so non-shared fields (assessments, behaviors, goals, etc.) prefill.
        if (versionId) {
          const ver = await getIepVersion(versionId);
          if (cancelled) return;
          const snapshot = ver?.snapshot_data || {};
          saveFormData('iep', snapshot);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[IepVersionEditor] failed to load version:', err);
        setError(err?.response?.data?.detail || err?.message || 'Failed to load IEP version');
      } finally {
        if (!cancelled) setHydrating(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [versionId, saveFormData, childId, updateSharedData]);

  if (hydrating) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="error" gutterBottom>
          {error}
        </Typography>
        <Button startIcon={<LeftOutlined />} onClick={() => navigate(returnPath)}>
          {fmt('common.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {isView && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<EditOutlined />}
            onClick={() => navigate(`${personaBasePath}/child/${childId}/iep/version/${versionId}/edit`)}
          >
            {fmt('iepDocs.editAsNewVersion')}
          </Button>
        </Stack>
      )}
      <IEP
        readOnly={isView}
        versioningMode="version"
        clientIdOverride={childId}
        returnPath={returnPath}
        // In edit mode, pass the versionId so the form updates this version
        // in place instead of spawning a new one. New-version mode (`mode==='new'`)
        // intentionally leaves this undefined so createClientIepVersion runs.
        editVersionId={mode === 'edit' ? versionId : null}
      />
    </Box>
  );
}

IepVersionEditorInner.propTypes = {
  personaBasePath: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(['new', 'view', 'edit']).isRequired
};

export default function IepVersionEditor(props) {
  return (
    <StudentRegistrationProvider>
      <IepVersionEditorInner {...props} />
    </StudentRegistrationProvider>
  );
}

IepVersionEditor.propTypes = {
  personaBasePath: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(['new', 'view', 'edit']).isRequired
};
